import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";

export const MoveDialog = ({ target, holderId = "me", onClose, onDone }) => {
  const [qty, setQty] = useState("1");
  const [to, setTo] = useState("");
  const [reason, setReason] = useState("");
  const [users, setUsers] = useState([]);
  const [busy, setBusy] = useState(false);
  const transfer = target?.mode === "transfer";
  useEffect(() => {
    setQty("1"); setTo(""); setReason("");
    if (transfer) api.get("/inventory/my/transfer-targets").then((r) => setUsers(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [target, transfer]);
  if (!target) return null;
  const submit = async () => {
    setBusy(true);
    const body = { item_id: target.item_id, serials: target.serial ? [target.serial] : [], quantity: target.serial ? 1 : Number(qty), reason, to_user_id: to || null };
    try {
      await api.post(transfer ? "/inventory/my/transfers" : holderId === "me" ? "/inventory/my/return" : `/inventory/holders/${holderId}/return`, body);
      toast.success(transfer ? "Transferencia enviada: queda pendiente hasta que la acepte" : "Devolución registrada");
      onDone(); onClose();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="holder-move-dialog">
        <DialogHeader><DialogTitle className="font-display">{transfer ? "Transferir a otro técnico" : "Devolver al almacén"}</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm">
          <p data-testid="holder-move-item">{target.label}{target.serial ? ` · ${target.serial}` : ""}{target.sim ? ` · con SIM ${target.sim}` : ""}</p>
          {!target.serial && <label className="block">Cantidad (máx. {target.max})<Input type="number" min="1" max={target.max} value={qty} onChange={(e) => setQty(e.target.value)} data-testid="holder-move-qty" /></label>}
          {transfer && <select className="h-10 w-full rounded-md border border-border bg-background px-3" value={to} onChange={(e) => setTo(e.target.value)} data-testid="holder-move-target">
            <option value="">{users.length ? "Elige el técnico que recibe…" : "No hay otros técnicos activos"}</option>
            {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>}
          <Textarea rows={2} placeholder="Motivo / observaciones" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="holder-move-reason" />
          <Button className="w-full" disabled={busy || (transfer && !to) || (!target.serial && !(Number(qty) > 0))} onClick={submit} data-testid="holder-move-submit">
            {transfer ? "Enviar transferencia" : "Registrar devolución"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const TransferRow = ({ t, children }) => (
  <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-amber-500/30 px-3 py-2" data-testid={`transfer-${t.id}`}>
    <div><div>{t.item_label} · {t.serials.length ? t.serials.join(", ") : `${t.quantity} ${t.unit || ""}`}</div>
      <div className="text-xs text-muted-foreground">{t.from_user_name} → {t.to_user_name}{t.companion_sims?.length ? ` · con SIM ${t.companion_sims.map((s) => s.serial).join(", ")}` : ""}{t.work_order_folios?.length ? ` · OS ${t.work_order_folios.join(", ")}` : ""}{t.reason ? ` · ${t.reason}` : ""}</div></div>
    <div className="flex gap-1">{children}</div>
  </div>
);

export const TransfersPanel = ({ userId, version, onChange }) => {
  const { can } = useAuth();
  const [d, setD] = useState(null);
  const self = userId === "me";
  const load = useCallback(() => api.get(`/inventory/holders/${userId}/transfers`).then((r) => setD(r.data)).catch(() => setD(null)), [userId]);
  useEffect(() => { load(); }, [load, version]);
  const act = async (id, a, msg) => { try { await api.post(`/inventory/transfers/${id}/${a}`); toast.success(msg); onChange(); } catch (e) { toast.error(errMsg(e)); onChange(); } };
  if (!d || (!d.incoming.length && !d.outgoing.length)) return null;
  return (
    <div className="space-y-2" data-testid="transfers-panel">
      {d.incoming.length > 0 && <h3 className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">Por recibir</h3>}
      {d.incoming.map((t) => <TransferRow key={t.id} t={t}>{self && <>
        <Button size="sm" className="h-7" onClick={() => act(t.id, "accept", "Transferencia aceptada")} data-testid={`transfer-accept-${t.id}`}>Aceptar</Button>
        <Button size="sm" variant="ghost" className="h-7 text-red-400" onClick={() => act(t.id, "reject", "Transferencia rechazada")} data-testid={`transfer-reject-${t.id}`}>Rechazar</Button></>}
      </TransferRow>)}
      {d.outgoing.length > 0 && <h3 className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">Enviadas, pendientes de aceptar</h3>}
      {d.outgoing.map((t) => <TransferRow key={t.id} t={t}>{(self || can("inventory.write")) &&
        <Button size="sm" variant="ghost" className="h-7" onClick={() => act(t.id, "cancel", "Transferencia cancelada")} data-testid={`transfer-cancel-${t.id}`}>Cancelar</Button>}
      </TransferRow>)}
    </div>
  );
};
